import React, { useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, Activity, TrendingUp, UserCheck, MessageSquare, AlertTriangle, FileCheck2, Award, Check, Sparkles } from 'lucide-react';

const DashboardFeatures = () => {
    const sliderRef = useRef(null);
    const prevBtnRef = useRef(null);
    const nextBtnRef = useRef(null);

    useEffect(() => {
        const slider = sliderRef.current;
        const prevBtn = prevBtnRef.current;
        const nextBtn = nextBtnRef.current;

        if (!slider) return;

        function updateArrows() {
            if (slider.scrollLeft > 20) {
                prevBtn.classList.remove("opacity-0", "pointer-events-none");
                prevBtn.classList.add("opacity-100");
            } else {
                prevBtn.classList.add("opacity-0", "pointer-events-none");
                prevBtn.classList.remove("opacity-100");
            }

            if (slider.scrollLeft + slider.clientWidth >= slider.scrollWidth - 20) {
                nextBtn.classList.add("opacity-0", "pointer-events-none");
                nextBtn.classList.remove("opacity-100");
            } else {
                nextBtn.classList.remove("opacity-0", "pointer-events-none");
                nextBtn.classList.add("opacity-100");
            }
        }

        slider.addEventListener("scroll", updateArrows);
        window.addEventListener("resize", updateArrows);

        let autoSlideInterval;

        function startAutoSlide() {
            autoSlideInterval = setInterval(() => {
                const cardWidth = slider.querySelector("div").offsetWidth + 24;
                if (slider.scrollLeft + slider.clientWidth >= slider.scrollWidth - 20) {
                    slider.scrollTo({
                        left: 0,
                        behavior: "smooth"
                    });
                } else {
                    slider.scrollBy({
                        left: cardWidth,
                        behavior: "smooth"
                    });
                }
            }, 5000);
        }

        function stopAutoSlide() {
            clearInterval(autoSlideInterval);
        }

        startAutoSlide();

        slider.addEventListener("mouseenter", stopAutoSlide);
        slider.addEventListener("mouseleave", startAutoSlide);
        slider.addEventListener("touchstart", stopAutoSlide, { passive: true });
        slider.addEventListener("touchend", startAutoSlide);

        const handlePrevClick = () => {
            stopAutoSlide();
            const cardWidth = slider.querySelector("div").offsetWidth + 24;
            slider.scrollBy({
                left: -cardWidth,
                behavior: "smooth"
            });
        };

        const handleNextClick = () => {
            stopAutoSlide();
            const cardWidth = slider.querySelector("div").offsetWidth + 24;
            slider.scrollBy({
                left: cardWidth,
                behavior: "smooth"
            });
        };

        prevBtn.addEventListener("click", handlePrevClick);
        nextBtn.addEventListener("click", handleNextClick);

        setTimeout(updateArrows, 150);

        return () => {
            slider.removeEventListener("scroll", updateArrows);
            window.removeEventListener("resize", updateArrows);
            slider.removeEventListener("mouseenter", stopAutoSlide);
            slider.removeEventListener("mouseleave", startAutoSlide);
            slider.removeEventListener("touchstart", stopAutoSlide);
            slider.removeEventListener("touchend", startAutoSlide);
            if (prevBtn) prevBtn.removeEventListener("click", handlePrevClick);
            if (nextBtn) nextBtn.removeEventListener("click", handleNextClick);
            clearInterval(autoSlideInterval);
        }
    }, []);

    return (
        <section id="dashboard" className="section-padding-lg bg-primary-light lg:min-h-screen flex flex-col justify-center overflow-x-hidden w-full">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
                <div className="max-w-4xl mb-12 sm:mb-16" data-aos="fade-up">
                    <h2 className="heading-lg mb-4 break-words block w-full max-w-full">
                        Software solutions powering your practice
                    </h2>
                    <p className="paragraph-lg max-w-2xl">
                        Replaces the opacity of traditional billing with radical visibility into your practice's financial
                        health.
                    </p>
                </div>

                <div className="relative group block w-full">
                    <button ref={prevBtnRef} id="slider-prev"
                        className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 -translate-x-1/2 bg-primary-light/95 backdrop-blur-sm text-secondary w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 rounded-full flex items-center justify-center cursor-pointer pointer-events-none opacity-0 z-[100] transition-all duration-300 hover:bg-gray-50 border-0">
                        <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
                    </button>
                    <button ref={nextBtnRef} id="slider-next"
                        className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 translate-x-1/2 bg-primary-light/95 backdrop-blur-sm text-secondary w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 rounded-full flex items-center justify-center cursor-pointer pointer-events-auto opacity-100 z-[100] transition-all duration-300 hover:bg-gray-50 border-0">
                        <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
                    </button>

                    <div ref={sliderRef} id="features-slider"
                        className="flex items-stretch overflow-x-auto snap-x snap-mandatory gap-6 lg:gap-8 pb-8 hide-scrollbar sm:px-4 [&>div]:flex-none [&>div]:snap-center [&>div]:w-[85vw] md:[&>div]:w-[45vw] lg:[&>div]:w-[30vw] xl:[&>div]:w-[380px] [&::-webkit-scrollbar]:hidden"
                        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>

                        {/* Feature 1: Real-Time Claim Tracking */}
                        <div className="bg-primary rounded-[2rem] px-8 lg:px-10 pt-8 lg:pt-10 pb-0 flex flex-col relative overflow-hidden group"
                            data-aos="fade-up" data-aos-delay="100">
                            <div className="flex items-center gap-3 mb-4 relative z-10">
                                <Activity className="w-6 h-6 text-secondary/60 flex-shrink-0" strokeWidth="1" />
                                <h3 className="heading-md">Real-Time Claim Tracking</h3>
                            </div>
                            <p className="paragraph-md relative z-10 mb-10">
                                See exactly where every dollar is in the cycle. Monitor Total Claims, 60-Day Claims, and
                                Zero-Pay Claims with live updates.
                            </p>
                            <div className="mt-auto h-40 bg-primary-light rounded-t-2xl p-6 relative z-0 mx-auto w-full max-w-[300px] flex flex-col gap-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex flex-col gap-1">
                                        <span className="text-sm text-gray-400 uppercase tracking-tighter">Claim #72410</span>
                                        <span className="text-sm font-semibold text-gray-900">$2,840.00</span>
                                    </div>
                                    <div className="text-accent text-sm font-bold uppercase tracking-tight">Processing</div>
                                </div>
                                <div className="flex items-center justify-between relative mt-2 px-1">
                                    <div className="absolute top-1/2 left-0 right-0 h-[1px] bg-gray-100 -translate-y-1/2"></div>
                                    <div className="relative z-10 w-2.5 h-2.5 bg-accent rounded-full border-2 border-primary-light ring-1 ring-accent/30"></div>
                                    <div className="relative z-10 w-4 h-4 bg-primary-light rounded-full border border-accent flex items-center justify-center">
                                        <div className="w-2 h-2 bg-accent rounded-full animate-pulse"></div>
                                    </div>
                                    <div className="relative z-10 w-2.5 h-2.5 bg-gray-100 rounded-full border-2 border-primary-light ring-1 ring-gray-100"></div>
                                    <div className="relative z-10 w-2.5 h-2.5 bg-gray-100 rounded-full border-2 border-primary-light ring-1 ring-gray-100"></div>
                                </div>
                                <div className="flex justify-between text-sm text-gray-400 font-medium px-0">
                                    <span>SUBMITTED</span>
                                    <span className="text-accent">PAYER</span>
                                    <span>REVIEW</span>
                                    <span>PAID</span>
                                </div>
                                <div className="mt-auto pt-3 border-t border-gray-50 flex items-center gap-3">
                                    <div className="w-6 h-6 rounded-lg bg-gray-50 flex items-center justify-center text-gray-300">
                                        <Check className="w-3.5 h-3.5" />
                                    </div>
                                    <div className="flex-1 space-y-1">
                                        <div className="w-full h-1 bg-gray-100 rounded-full overflow-hidden">
                                            <div className="w-2/3 h-full bg-accent rounded-full"></div>
                                        </div>
                                        <div className="w-1/2 h-1 bg-gray-50 rounded-full"></div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Feature 2 */}
                        <div className="bg-primary rounded-[2rem] px-8 lg:px-10 pt-8 lg:pt-10 pb-0 flex flex-col relative overflow-hidden group"
                            data-aos="fade-up" data-aos-delay="200">
                            <div className="flex items-center gap-3 mb-4 relative z-10">
                                <TrendingUp className="w-6 h-6 text-secondary/60 flex-shrink-0" strokeWidth="1" />
                                <h3 className="heading-md">Revenue Projections</h3>
                            </div>
                            <p className="paragraph-md relative z-10 mb-10">
                                Accurately forecast your cash flow based on live data and historical payment trends. Stay informed at all times.
                            </p>
                            <div className="mt-auto h-40 bg-primary-light rounded-t-2xl p-6 relative z-0 mx-auto w-full max-w-[300px] flex flex-col gap-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex flex-col gap-1">
                                        <span className="text-sm text-gray-400 uppercase tracking-tighter">Projected Q3</span>
                                        <span className="text-sm font-semibold text-gray-900">$142,500.00</span>
                                    </div>
                                    <div className="flex items-center gap-1 text-accent text-sm font-bold">
                                        <TrendingUp className="w-3 h-3" strokeWidth="1" />+14.2%
                                    </div>
                                </div>
                                <div className="flex items-end justify-between h-16 gap-2 px-1">
                                    <div className="flex-1 bg-gray-100 rounded-t-md h-[40%]"></div>
                                    <div className="flex-1 bg-gray-100 rounded-t-md h-[55%]"></div>
                                    <div className="flex-1 bg-gray-200 rounded-t-md h-[45%]"></div>
                                    <div className="flex-1 bg-accent rounded-t-md h-[75%] relative">
                                        <span className="absolute -top-4 left-1/2 -translate-x-1/2 text-sm font-bold text-accent">NOW</span>
                                    </div>
                                    <div className="flex-1 border-t-2 border-x-2 border-dashed border-secondary/20 rounded-t-md h-[95%] bg-secondary/5 relative">
                                        <span className="absolute h-full w-full flex items-center justify-center">
                                            <Sparkles className="w-3 h-3 text-secondary/20" strokeWidth="1" />
                                        </span>
                                    </div>
                                </div>
                                <div className="flex justify-between text-sm text-gray-400 font-medium px-1">
                                    <span>MAY</span>
                                    <span>JUN</span>
                                    <span>JUL</span>
                                    <span className="text-accent">AUG</span>
                                    <span className="text-secondary/40">SEP</span>
                                </div>
                            </div>
                        </div>

                        {/* Feature 3 */}
                        <div className="bg-primary rounded-[2rem] px-8 lg:px-10 pt-8 lg:pt-10 pb-0 flex flex-col relative overflow-hidden group"
                            data-aos="fade-up" data-aos-delay="300">
                            <div className="flex items-center gap-3 mb-4 relative z-10">
                                <UserCheck className="w-6 h-6 text-secondary/60 flex-shrink-0" strokeWidth="1" />
                                <h3 className="heading-md">Access to a Specialist</h3>
                            </div>
                            <p className="paragraph-md relative z-10 mb-10">
                                You'll never be a ticket number. Have a question about a claim? You have a direct line to the specialist handling it.
                            </p>
                            <div className="mt-auto h-40 bg-primary-light rounded-t-2xl p-5 relative z-0 mx-auto w-full max-w-[300px] flex flex-col gap-3">
                                <div className="flex items-center justify-between border-b border-gray-50 pb-2 mb-1">
                                    <div className="flex items-center gap-2">
                                        <div className="w-5 h-5 bg-accent/10 rounded-full flex items-center justify-center">
                                            <MessageSquare className="w-2.5 h-2.5 text-accent" strokeWidth="1" />
                                        </div>
                                        <span className="text-sm font-bold text-gray-400 uppercase tracking-widest">Direct Message</span>
                                    </div>
                                    <span className="text-sm text-accent font-bold uppercase tracking-tight flex items-center gap-1">
                                        <span className="w-1 h-1 bg-accent rounded-full"></span>Online
                                    </span>
                                </div>
                                <div className="space-y-3">
                                    <div className="flex items-end gap-2 pr-4">
                                        <div className="w-6 h-6 rounded-full bg-gray-100 flex-shrink-0 border border-gray-200 overflow-hidden flex items-center justify-center">
                                            <span className="text-[10px] font-bold text-accent">V</span>
                                        </div>
                                        <div className="bg-gray-50 p-2.5 rounded-2xl rounded-bl-sm border border-gray-100 space-y-1">
                                            <div className="w-24 h-1.5 bg-gray-200 rounded-full"></div>
                                            <div className="w-16 h-1.5 bg-gray-200 rounded-full"></div>
                                        </div>
                                    </div>
                                    <div className="flex flex-col items-end gap-1 pl-4">
                                        <div className="bg-accent p-2.5 rounded-2xl rounded-br-sm space-y-1 w-full max-w-[140px]">
                                            <div className="w-full h-1.5 bg-primary-light/30 rounded-full"></div>
                                            <div className="w-2/3 h-1.5 bg-primary-light/30 rounded-full"></div>
                                        </div>
                                        <span className="text-sm text-gray-400 font-medium">Delivered · 2m ago</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Feature 4 */}
                        <div className="bg-primary rounded-[2rem] px-8 lg:px-10 pt-8 lg:pt-10 pb-0 flex flex-col relative overflow-hidden group"
                            data-aos="fade-up" data-aos-delay="400">
                            <div className="flex items-center gap-3 mb-4 relative z-10">
                                <AlertTriangle className="w-6 h-6 text-secondary/60 flex-shrink-0" strokeWidth="1" />
                                <h3 className="heading-md">Denial Rate Monitoring</h3>
                            </div>
                            <p className="paragraph-md relative z-10 mb-10">
                                Track Total Denials and Denial Rates down to the specific provider or procedure to correct systemic issues at the source.
                            </p>
                            <div className="mt-auto h-40 bg-primary-light rounded-t-2xl p-6 relative z-0 mx-auto w-full max-w-[300px] flex flex-col gap-4">
                                <div className="flex items-center gap-5">
                                    <div className="relative w-16 h-16 flex items-center justify-center">
                                        <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36">
                                            <circle cx="18" cy="18" r="16" fill="none" className="stroke-gray-100" strokeWidth="3"></circle>
                                            <circle cx="18" cy="18" r="16" fill="none" className="stroke-accent" strokeWidth="3" strokeDasharray="25, 100" strokeLinecap="round"></circle>
                                        </svg>
                                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                                            <span className="text-sm font-bold text-gray-900 leading-none">2.4%</span>
                                            <span className="text-[10px] text-gray-400 uppercase font-bold mt-0.5">Rate</span>
                                        </div>
                                    </div>
                                    <div className="flex-1 space-y-1">
                                        <div className="flex items-center justify-between">
                                            <span className="text-[10px] text-gray-400 font-bold uppercase tracking-tighter">System Health</span>
                                            <span className="text-[10px] text-accent font-bold tracking-tight">EXCELLENT</span>
                                        </div>
                                        <div className="w-full h-1 bg-gray-50 rounded-full overflow-hidden">
                                            <div className="w-full h-full bg-accent rounded-full opacity-20"></div>
                                        </div>
                                        <p className="text-[10px] text-gray-400 leading-tight">Industry Average: 9.8%</p>
                                    </div>
                                </div>
                                <div className="space-y-2 mt-auto">
                                    <div className="flex items-center gap-2">
                                        <div className="w-1.5 h-1.5 bg-accent rounded-full"></div>
                                        <div className="flex-1 h-2 bg-gray-50 rounded-full relative overflow-hidden">
                                            <div className="absolute inset-0 w-3/4 bg-accent/20 rounded-full"></div>
                                        </div>
                                        <span className="text-sm font-bold text-gray-400">75%</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-1.5 h-1.5 bg-gray-300 rounded-full"></div>
                                        <div className="flex-1 h-2 bg-gray-50 rounded-full relative overflow-hidden">
                                            <div className="absolute inset-0 w-1/4 bg-gray-200 rounded-full"></div>
                                        </div>
                                        <span className="text-sm font-bold text-gray-400">25%</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Feature 5 */}
                        <div className="bg-primary rounded-[2rem] px-8 lg:px-10 pt-8 lg:pt-10 pb-0 flex flex-col relative overflow-hidden group"
                            data-aos="fade-up" data-aos-delay="500">
                            <div className="flex items-center gap-3 mb-4 relative z-10">
                                <FileCheck2 className="w-6 h-6 text-secondary/60 flex-shrink-0" strokeWidth="1" />
                                <h3 className="heading-md">Total Adjudicated Claims</h3>
                            </div>
                            <p className="paragraph-md relative z-10 mb-10">
                                Gain insights into your successfully processed claims with an overarching view of your adjudicated payments.
                            </p>
                            <div className="mt-auto h-40 bg-primary-light rounded-t-2xl p-6 relative z-0 mx-auto w-full max-w-[300px] flex flex-col gap-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex flex-col gap-1">
                                        <span className="text-sm text-gray-400 uppercase tracking-tighter">Monthly Total</span>
                                        <span className="text-sm font-semibold text-gray-900">12,408 Claims</span>
                                    </div>
                                    <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center">
                                        <Award className="w-4 h-4 text-accent" strokeWidth="1" />
                                    </div>
                                </div>
                                <div className="space-y-3 mt-auto">
                                    <div className="bg-gray-50 border border-gray-100 rounded-xl p-3 flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="w-5 h-5 bg-primary-light rounded-lg border border-gray-100 flex items-center justify-center">
                                                <Check className="w-2.5 h-2.5 text-accent" strokeWidth="1" />
                                            </div>
                                            <div className="flex flex-col gap-1">
                                                <span className="text-sm font-bold text-gray-500 uppercase tracking-tighter">Batch #A-842</span>
                                                <div className="w-16 h-1 bg-accent/20 rounded-full"></div>
                                            </div>
                                        </div>
                                        <span className="text-sm font-bold text-accent">VERIFIED</span>
                                    </div>
                                    <div className="bg-gray-50/50 border border-gray-100/50 rounded-xl p-3 flex items-center justify-between opacity-60">
                                        <div className="flex items-center gap-3">
                                            <div className="w-5 h-5 bg-primary-light rounded-lg border border-gray-100 flex items-center justify-center">
                                                <Check className="w-2.5 h-2.5 text-accent" strokeWidth="1" />
                                            </div>
                                            <div className="w-20 h-1.5 bg-gray-200 rounded-full"></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Feature 6 (CTA) */}
                        <div className="bg-motion-dark rounded-[2rem] p-8 lg:p-10 flex flex-col relative overflow-hidden group text-center justify-center items-center border border-accent/20"
                            data-aos="fade-up" data-aos-delay="600">
                            <h3 className="heading-md text-white mb-6">Ready to replace "black-box" billing?</h3>
                            <p className="paragraph-md text-primary-dark mb-8 max-w-xs mx-auto">
                                Get started with a free financial audit to identify missing revenue.
                            </p>
                            <a href="#contact" className="btn-primary-md">
                                Request a Demo
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default DashboardFeatures;
